import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import type { ToolCall } from '@/types/conversation'
import { ToolCallScreenshotLink } from './ToolCallScreenshotLink'

interface ToolCallDisplayProps {
  toolCall: ToolCall
  /** Content of the matching tool response message */
  response?: string | null
  /** Assistant configuration (for checking screenshot support) */
  assistantConfig?: unknown
  /** Date when the conversation took place (for screenshot URL generation) */
  conversationDate?: string
  className?: string
}

export function ToolCallDisplay({
  toolCall,
  response,
  assistantConfig,
  conversationDate,
  className,
}: ToolCallDisplayProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  let parsedArgs: Record<string, unknown> = {}
  try {
    parsedArgs = JSON.parse(toolCall.function.arguments)
  } catch {
    // If parsing fails, show raw arguments
  }

  return (
    <div className={cn('mt-2 rounded-md bg-brand-50 p-2 dark:bg-brand-950', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-left text-sm"
      >
        <span className="font-medium text-brand-700 dark:text-brand-300">
          {toolCall.function.name}
        </span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 transition-transform text-brand-600',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && (
        <div className="mt-2 space-y-2">
          <div className="rounded border border-brand-200 bg-white p-2 dark:border-brand-800 dark:bg-gray-900">
            <pre className="whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">
              {Object.keys(parsedArgs).length > 0
                ? Object.entries(parsedArgs)
                    .map(
                      ([key, value]) =>
                        `${key}: ${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}`
                    )
                    .join('\n')
                : toolCall.function.arguments}
            </pre>
          </div>
          {response !== undefined && (
            <div className="rounded border-l-2 border-brand-500 bg-white p-2 dark:bg-gray-900">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {t('resources.conversations.transcript.response')}
              </span>
              <p className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">
                {response}
              </p>
              {/* Screenshot link for availability tool calls */}
              <ToolCallScreenshotLink
                toolCallId={toolCall.id}
                toolName={toolCall.function.name}
                conversationDate={conversationDate}
                assistantConfig={assistantConfig}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ToolCallDisplay
